/**
 * Формирование счёта и акта из выбранных заказов — §2.
 *
 * Два предупреждения, оба обязательны и оба не блокируют действие:
 *
 *   §2.2 — при первом счёте в системе подсказка о ручной правке номера.
 *          Это единственный способ мигрировать нумерацию из прежней программы
 *          лаборатории: у них уже был счёт №367, и новый должен стать 368.
 *   §2.4 — если заказы из разных месяцев, перечисляются все месяцы. Объединять
 *          разрешено (работа часто выставляется не в месяц приёма), но
 *          пользователь должен видеть, что это осознанно.
 *
 * Превью документа рендерится тем же кодом, что и серверный PDF, — расхождение
 * между тем, что видно на экране, и тем, что уйдёт в клинику, исключено.
 */

import { DocumentPreview } from '../components/DocumentPreview.js';
import { useEffect, useMemo, useState } from 'react';
import { Plus, X } from 'lucide-react';
import {
  buildDocument,
  deliveryLast,
  buildInvoiceItems,
  clinicDeliveryItems,
  withInvoiceDelivery,

  formatKop,
  invoiceMonthSpan,
  formatMonthKey,
  nameIndex,

  rublesToKop,
  todayIso,
  type DocumentKind,
  type Legal,
  type Performer,
  legalTitleText,
  missingInvoiceRequisites,
} from '@otc/shared';
import { api } from '../api/client.js';
import type { ApiOrder, Bootstrap } from '../api/queries.js';
import { Button, Modal } from '../components/ui.js';
import { hasDraftWorkChanges, regroupDraftItems, retitleDraftItems } from './invoice-draft.js';

interface EditableItem {
  id: string;
  name: string;
  qty: number;
  unit: string;
  priceKop: number;
}

export function InvoiceCreateModal({
  orderIds,
  orders,
  bootstrap,
  onClose,
  onCreated,
}: {
  orderIds: string[];
  orders: ApiOrder[];
  bootstrap: Bootstrap;
  onClose: () => void;
  /**
   * Счёт создан. Ключ нужен, чтобы сразу открыть сам счёт: закрывать окно
   * и оставлять человека в списке — значит заставить его искать только что
   * созданное, чтобы отправить письмо.
   */
  onCreated: (invoice: { id: string; number: number }) => void;
}) {
  const today = todayIso();
  const chosen = orders.filter((order) => orderIds.includes(order.id));

  const catalog = useMemo(() => nameIndex(bootstrap.constructions), [bootstrap.constructions]);

  const first = chosen[0];
  const clinic = bootstrap.clinics.find((c) => c.id === first?.clinicId);
  const branch = clinic?.branches.find((b) => b.id === first?.branchId);
  const legal = clinic?.legals.find((l) => l.id === branch?.legalId);
  const performer = bootstrap.settings?.performer as unknown as Performer | undefined;

  /**
   * Сводить работы в одну строку — настройка юрлица, но здесь её можно
   * переключить: режим предлагается по клинике, а решение остаётся за тем,
   * кто выставляет счёт. Разовое исключение не должно требовать похода
   * в справочник и обратно.
   */
  const [merged, setMerged] = useState(() => legal?.mergeInvoiceItems ?? false);

  /*
   * Фамилии в строках — тоже по юрлицу и тоже переключаются разово. Пациент
   * по умолчанию есть, врач — нет: так выставлялись все счета до сих пор, а
   * клиникам, что сверяют счёт по врачам, врача включают в их карточке.
   */
  const [withPatient, setWithPatient] = useState(() => legal?.invoicePatient ?? true);
  const [withDoctor, setWithDoctor] = useState(() => legal?.invoiceDoctor ?? false);

  /** Заказы с ФИО врача: в строку счёта он попадает, когда включён. */
  const invoiceable = useMemo(() => {
    const doctors = new Map(bootstrap.doctors.map((d) => [d.id, d.name]));
    return chosen.map((order) => ({ ...order, doctor: doctors.get(order.doctorId) ?? '' }));
    // Выбор заказов на время окна не меняется.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bootstrap.doctors]);

  const build = (merge: boolean, patient: boolean, doctor: boolean) =>
    buildInvoiceItems(invoiceable, catalog, { merge, patient, doctor });

  /**
   * Позиции заполняются из заказов, но дальше живут независимо (§2.1):
   * их можно править, удалять и добавлять, не затрагивая заказы.
   */
  const [items, setRawItems] = useState<EditableItem[]>(() =>
    withInvoiceDelivery(build(merged, withPatient, withDoctor), clinicDeliveryItems(clinic)),
  );

  const setItems = (next: Parameters<typeof setRawItems>[0]) => setRawItems(current => deliveryLast(typeof next === 'function' ? next(current) : next));
  const [pendingMode, setPendingMode] = useState<boolean | null>(null);
  const applyMode = (next: boolean) => {
    setItems((current) => regroupDraftItems(current, build(merged, withPatient, withDoctor), build(next, withPatient, withDoctor)));
    setMerged(next);
    setPendingMode(null);
  };
  const switchMode = (next: boolean) => {
    if (next === merged) return;
    if (hasDraftWorkChanges(items, build(merged, withPatient, withDoctor))) setPendingMode(next);
    else applyMode(next);
  };
  const switchPeople = (patient: boolean, doctor: boolean) => {
    setItems((current) => retitleDraftItems(current, build(merged, withPatient, withDoctor), build(merged, patient, doctor)));
    setWithPatient(patient);
    setWithDoctor(doctor);
  };

  const [number, setNumber] = useState<number | null>(null);
  const [isFirstEver, setIsFirstEver] = useState(false);
  /** Почему номер не подставился. Пусто, когда всё в порядке. */
  const [numberError, setNumberError] = useState<string | null>(null);
  const [tab, setTab] = useState<DocumentKind>('schet');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  /**
   * Номер по умолчанию приходит с сервера: он считается от МАКСИМУМА в году,
   * а не от количества счетов (§2.2). Подставляется только если пользователь
   * ещё не правил поле вручную.
   */
  useEffect(() => {
    let cancelled = false;
    api
      .get<{ number: number; isFirstEver: boolean }>(`/api/invoices/next-number?date=${today}`)
      .then((result) => {
        if (cancelled) return;
        setNumber((current) => current ?? result.number);
        setNumberError(null);
        setIsFirstEver(result.isFirstEver);
      })
      .catch(() => {
        /*
          НЕ подставляем 1 «на всякий случай».

          Раньше при недоступном сервере в поле молча вставала единица — и это
          хуже пустого поля: в 2026 году №1 давно занят, счёт бы не создался,
          а человек увидел бы отказ по уникальности вместо настоящей причины.
          Единственный случай, когда 1 верна, — самый первый счёт в базе,
          и тогда её пришлёт сам сервер.

          Поле остаётся пустым, кнопка создания уже заблокирована при пустом
          номере, а рядом появляется объяснение, что произошло.
        */
        if (!cancelled) setNumberError('Сервер не ответил, следующий номер неизвестен. Обновите страницу.');
      });
    return () => {
      cancelled = true;
    };
  }, [today]);

  const span = invoiceMonthSpan(chosen.map((order) => ({ received: order.received })));

  const model = useMemo(() => {
    if (!performer || !legal) return null;
    return buildDocument({
      kind: tab,
      performer,
      legal: legal as unknown as Legal,
      clinicName: clinic?.name ?? '',
      number: number ?? 1,
      date: today,
      items: items.map((item) => ({
        name: item.name,
        qty: item.qty,
        unit: item.unit,
        priceKop: item.priceKop,
      })),
    });
  }, [performer, legal, clinic, number, today, items, tab]);

  const create = async () => {
    if (!number) return;
    setSaving(true);
    setError(null);
    try {
      const created = await api.post<{ invoice: { id: string } }>('/api/invoices', {
        orderIds,
        number,
        date: today,
        items: items.map((item, index) => ({
          id: item.id,
          position: index,
          name: item.name,
          qty: item.qty,
          unit: item.unit,
          priceKop: item.priceKop,
        })),
      });
      onCreated({ id: created.invoice.id, number });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось сформировать счёт');
    } finally {
      setSaving(false);
    }
  };

  const totalKop = items.reduce((sum, item) => sum + Math.round(item.priceKop * item.qty), 0);

  const missingRequisites = legal ? missingInvoiceRequisites(legal) : [];

  if (!performer || !legal) {
    return (
      <Modal title="Не хватает данных" onClose={onClose} size="sm">
        <p className="pb-4 text-sm text-ink2">
          {!performer
            ? 'Не заданы реквизиты исполнителя. Заполните их в «Документы → Реквизиты».'
            : 'У заказа не определилось юрлицо. Проверьте филиал клиники.'}
        </p>
      </Modal>
    );
  }

  return (
    <Modal
      title={`Новый счёт и акт №${number ?? '…'}`}
      onClose={onClose}
      size="xl"
      footer={
        <>
          <Button onClick={() => void create()} disabled={saving || !number || items.length === 0}>
            {saving ? 'Формирование…' : 'Сформировать счёт и акт'}
          </Button>
          <Button variant="secondary" onClick={onClose}>Отмена</Button>
          <span className="ml-auto self-center text-sm text-ink2">
            Итого: <span className="tabular text-ink">{formatKop(totalKop)} ₽</span>
          </span>
        </>
      }
    >
      {/* §2.2 — подсказка о миграции нумерации */}
      {isFirstEver && (
        <div className="mb-3 rounded-lg px-3 py-2 text-xs text-ink" style={{ background: 'rgba(15,107,102,0.08)' }}>
          Это первый счёт в системе. Если раньше вели нумерацию в другой программе — поставьте нужный
          номер вручную: дальше система продолжит считать сама от этого числа.
        </div>
      )}

      {/* §2.4 — объединение заказов разных месяцев */}
      {span.spansMultipleMonths && (
        <div className="mb-3 rounded-lg px-3 py-2 text-xs text-ink" style={{ background: 'rgba(201,133,44,0.12)' }}>
          В этот счёт объединены заказы из разных месяцев ({span.months.map(formatMonthKey).join(', ')}).
          Номер и дата присваиваются по дате оформления счёта — это нормально.
        </div>
      )}

      <div className="mb-3 flex flex-wrap items-center gap-3">
        <label className="flex items-center gap-2 text-sm text-ink2">
          Номер
          <input
            type="number"
            className="otc-input tabular w-24"
            value={number ?? ''}
            onChange={(e) => setNumber(Number(e.target.value) || null)}
          />
        </label>
        <span className="text-sm text-ink2">
          {clinic?.name} · {legalTitleText(legal)}
        </span>
      </div>

      {numberError && (
        <div
          className="mb-3 rounded-lg px-3 py-2 text-xs"
          style={{ background: 'rgba(214,99,80,0.12)', color: 'var(--otc-coral)' }}
        >
          {numberError}
        </div>
      )}

      {/*
        Незаполненные реквизиты заказчика — ПРЕДУПРЕЖДЕНИЕ, а не запрет.
        
        Счёт всё равно печатается, просто с пустыми полями, и бывает, что так
        и надо: реквизиты пришлют завтра, а счёт нужен сегодня. Но узнать об
        этом лучше здесь, чем от клиники, которая получила бумагу с пустым
        ИНН. Проверка молчит для наличных: им счёт не выставляют вовсе.
      */}
      {missingRequisites.length > 0 && (
        <p
          className="mb-2 rounded-lg px-3 py-2 text-xs"
          style={{
            background: 'rgba(201,133,44,0.10)',
            color: 'var(--otc-amber, #AE5E1E)',
          }}
        >
          У заказчика не заполнено: {missingRequisites.join(', ')}. Счёт сформируется, но эти
          поля в нём останутся пустыми — реквизиты правятся в карточке клиники.
        </p>
      )}

      {/*
        Режим строк. Предлагается по настройке юрлица, но переключаем здесь:
        разовое исключение не должно требовать правки справочника.
      */}
      <div className="mb-2 flex flex-wrap items-center gap-2">
        {(
          [
            { value: false, label: 'По заказам', hint: `${chosen.length} строк` },
            { value: true, label: 'Одной строкой', hint: 'общей суммой' },
          ] as const
        ).map((mode) => (
          <button
            key={String(mode.value)}
            onClick={() => switchMode(mode.value)}
            className="rounded-md border px-3 py-1.5 text-xs transition-colors"
            style={{
              background: merged === mode.value ? 'var(--otc-accent-dark)' : 'var(--otc-surface)',
              color: merged === mode.value ? '#fff' : 'var(--otc-ink2)',
              borderColor: merged === mode.value ? 'var(--otc-accent-dark)' : 'var(--otc-line)',
            }}
          >
            {mode.label} <span className="opacity-70">· {mode.hint}</span>
          </button>
        ))}
        {merged !== (legal.mergeInvoiceItems ?? false) && (
          <span className="text-[11px] text-ink2">
            {legal.mergeInvoiceItems
              ? 'у этого юрлица по умолчанию — одной строкой'
              : 'у этого юрлица по умолчанию — по заказам'}
          </span>
        )}

        {/*
          Фамилии — только для строк по заказам: у сводной строки их нет.
          Отличие от настройки юрлица подсвечено, чтобы разовое исключение
          не выглядело как его обычный порядок.
        */}
        {!merged && (
          <span className="flex flex-wrap items-center gap-3 sm:ml-auto">
            {(
              [
                ['ФИО пациента', withPatient, legal.invoicePatient ?? true, () => switchPeople(!withPatient, withDoctor)],
                ['ФИО врача', withDoctor, legal.invoiceDoctor ?? false, () => switchPeople(withPatient, !withDoctor)],
              ] as const
            ).map(([label, on, byDefault, toggle]) => (
              <label key={label} className="inline-flex cursor-pointer items-center gap-1.5 text-xs text-ink">
                <input type="checkbox" checked={on} onChange={toggle} />
                {label}
                {on !== byDefault && (
                  <span className="text-[11px]" style={{ color: 'var(--otc-amber)' }}>
                    {byDefault ? '(у клиники включено)' : '(у клиники выключено)'}
                  </span>
                )}
              </label>
            ))}
          </span>
        )}
      </div>

      {pendingMode !== null && (
        <div role="alert" className="mb-4 rounded-lg border p-3 text-sm text-ink">
          <p>При смене вида строки работ будут заново заполнены из заказов. Их ручные цены, названия и количества сбросятся, удалённые работы вернутся. Доставка и добавленные строки сохранятся.</p>
          <div className="mt-2 flex gap-2">
            <Button onClick={() => setPendingMode(null)}>Оставить правки</Button>
            <Button onClick={() => applyMode(pendingMode)}>Пересобрать строки</Button>
          </div>
        </div>
      )}
      {/* Позиции — независимый список (§2.1) */}
      <div className="mb-4 rounded-lg border" style={{ borderColor: 'var(--otc-line)' }}>
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b" style={{ borderColor: 'var(--otc-line)' }}>
              <th className="px-2 py-1.5 text-left font-medium text-ink2">Наименование</th>
              <th className="w-16 px-2 py-1.5 text-right font-medium text-ink2">Кол-во</th>
              <th className="w-16 px-2 py-1.5 text-right font-medium text-ink2">Ед</th>
              <th className="w-28 px-2 py-1.5 text-right font-medium text-ink2">Цена, ₽</th>
              <th className="w-8" />
            </tr>
          </thead>
          <tbody>
            {items.map((item, index) => (
              <tr key={`${item.id}-${index}`} className="border-b" style={{ borderColor: 'var(--otc-line)' }}>
                <td className="px-2 py-1">
                  <input
                    className="w-full rounded border px-1.5 py-1 text-xs"
                    style={{ borderColor: 'var(--otc-line)' }}
                    value={item.name}
                    onChange={(e) =>
                      setItems(items.map((it, i) => (i === index ? { ...it, name: e.target.value } : it)))
                    }
                  />
                </td>
                <td className="px-2 py-1">
                  <input
                    type="number"
                    className="tabular w-full rounded border px-1.5 py-1 text-right text-xs"
                    style={{ borderColor: 'var(--otc-line)' }}
                    value={item.qty}
                    onChange={(e) =>
                      setItems(items.map((it, i) => (i === index ? { ...it, qty: Number(e.target.value) || 0 } : it)))
                    }
                  />
                </td>
                <td className="px-2 py-1">
                  <input
                    className="w-full rounded border px-1.5 py-1 text-right text-xs"
                    style={{ borderColor: 'var(--otc-line)' }}
                    value={item.unit}
                    onChange={(e) =>
                      setItems(items.map((it, i) => (i === index ? { ...it, unit: e.target.value } : it)))
                    }
                  />
                </td>
                <td className="px-2 py-1">
                  <input
                    className="tabular w-full rounded border px-1.5 py-1 text-right text-xs"
                    style={{ borderColor: 'var(--otc-line)' }}
                    value={formatKop(item.priceKop)}
                    onChange={(e) =>
                      setItems(items.map((it, i) => (i === index ? { ...it, priceKop: rublesToKop(e.target.value) } : it)))
                    }
                  />
                </td>
                <td className="px-1 text-right">
                  <button
                    onClick={() => setItems(items.filter((_, i) => i !== index))}
                    className="rounded p-0.5 hover:bg-black/5"
                    aria-label="Удалить позицию"
                  >
                    <X size={12} style={{ color: 'var(--otc-coral)' }} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <button
          onClick={() =>
            setItems([...items, { id: `new_${items.length}`, name: 'Новая позиция', qty: 1, unit: '', priceKop: 0 }])
          }
          className="m-2 inline-flex items-center gap-1 rounded-md border px-2.5 py-1.5 text-xs"
          style={{ borderColor: 'var(--otc-line)', color: 'var(--otc-accent)' }}
        >
          <Plus size={12} /> Добавить позицию
        </button>
      </div>

      {/* Превью — тот же рендер, что уходит в PDF */}
      <div className="mb-2 flex gap-1">
        {(['schet', 'akt'] as const).map((kind) => (
          <button
            key={kind}
            onClick={() => setTab(kind)}
            className="rounded-md border px-3 py-1.5 text-sm"
            style={{
              background: tab === kind ? 'var(--otc-accent-dark)' : 'var(--otc-surface)',
              color: tab === kind ? '#fff' : 'var(--otc-ink2)',
              borderColor: tab === kind ? 'var(--otc-accent-dark)' : 'var(--otc-line)',
            }}
          >
            {kind === 'schet' ? 'Счёт' : 'Акт'}
          </button>
        ))}
      </div>

      {model && <DocumentPreview model={model} />}

      {error && (
        <p className="pb-3 text-sm" style={{ color: 'var(--otc-coral)' }} role="alert">
          {error}
        </p>
      )}
    </Modal>
  );
}
